"use client";

import { useRef } from "react";
import { Heading, Text } from "@pamperedchef/design-system";

import {
	FilterChips,
	SortControl,
	selectAppliedFilters,
	type SortControlPresentation
} from "@/composites/product-listing";

import { useListingState, useListingTotal } from "../../listing-state";
import { LISTING_SORT_OPTIONS } from "../../listing-search";
import { ListingFilters } from "../FilterRail";

import * as styles from "./ResultsToolbar.module.scss";
import { useHideOnScroll } from "./use-hide-on-scroll";

function ListingSort({
	presentation
}: {
	presentation: SortControlPresentation;
}): JSX.Element | null {
	const { sort, setSort } = useListingState();

	return (
		<SortControl
			options={LISTING_SORT_OPTIONS}
			selected={sort}
			onChange={setSort}
			presentation={presentation}
		/>
	);
}

function ListingAppliedFilters(): JSX.Element | null {
	const { groups, toggle, clearAll } = useListingState();

	return (
		<FilterChips
			applied={selectAppliedFilters(groups)}
			onRemove={(filter) => toggle(filter.groupId, filter.value)}
			onClearAll={clearAll}
		/>
	);
}

export interface ResultsToolbarProps {
	showCount: boolean;
	heading?: string;
}

export function ResultsToolbar({
	showCount,
	heading
}: ResultsToolbarProps): JSX.Element {
	const total = useListingTotal();
	const barRef = useRef<HTMLDivElement>(null);
	const { stuck, hidden } = useHideOnScroll(barRef);

	return (
		<div
			ref={barRef}
			className={styles["toolbar"]}
			data-component="ResultsToolbar"
			data-stuck={stuck || undefined}
			data-hidden={hidden || undefined}
		>
			{heading ? (
				<Heading tag="h2" className={styles["toolbar__heading"]}>
					{heading}
				</Heading>
			) : null}
			<div className={styles["toolbar__row"]}>
				{showCount ? (
					<Text
						tag="p"
						className={styles["toolbar__count"]}
						aria-live="polite"
					>
						Showing <strong>{total}</strong>{" "}
						{total === 1 ? "result" : "results"}
					</Text>
				) : null}
				<div className={styles["toolbar__actions"]}>
					<div className={styles["toolbar__drawer"]}>
						<ListingFilters presentation="drawer" />
					</div>
					<div className={styles["toolbar__drawer"]}>
						<ListingSort presentation="sheet" />
					</div>
					<div className={styles["toolbar__menu"]}>
						<ListingSort presentation="menu" />
					</div>
				</div>
			</div>
			<div className={styles["toolbar__chips"]}>
				<ListingAppliedFilters />
			</div>
		</div>
	);
}
